
















































































































































































import '@/assets/scss/pages/dashboard-ecommerce.scss';

import { Component, Watch } from 'vue-property-decorator';
import { BaseMixin } from '@/mixins/BaseMixin';
import { mixins } from 'vue-class-component';
import { ListIcon, PlusIcon, GridIcon, XIcon, ArchiveIcon } from 'vue-feather-icons';

import DamageOverview from './DamageOverview.vue';
import CreateDamageModal from '@/views/pages/damages/CreateDamageModal.vue';
import { DamageFilter, DamageStatus, IDamage, IProduct, ITag, IUser } from 'admiral-core';
import { DamageApi } from '@/services/DamageApi';
import DamageKanban from '@/views/pages/damages/DamageKanban.vue';
import EditDamageModal from '@/views/pages/damages/EditDamageModal.vue';
import DamageArchivePopup from '@/views/pages/damages/DamageArchivePopup.vue';
import { PreferencesAction } from '@/store/PreferencesModule';
import BasicStatisticCard from '@/components/common/BasicStatisticCard.vue';
import { FormatMixin } from '@/mixins/FormatMixin';

@Component({
  components: {
    BasicStatisticCard,
    DamageKanban,
    CreateDamageModal,
    DamageOverview,
    EditDamageModal,
    DamageArchivePopup,
    ListIcon,
    PlusIcon,
    GridIcon,
    XIcon,
    ArchiveIcon,
  },
})
export default class Damages extends mixins(BaseMixin, FormatMixin) {
  damages: IDamage[] = [];
  isLoading = false;
  selectedDamage: IDamage | null = null;
  searchQuery = '';
  showArchivePopup = false;

  get damageFilter(): DamageFilter {
    return this.$store.getters['PreferencesModule/getDamageFilter'];
  }

  get showDemo(): boolean {
    return this.$store.getters['PreferencesModule/getShowDemo'];
  }

  @Watch('showDemo')
  onShowDemoChange() {
    this.loadDamages();
  }

  get viewMode(): 'list' | 'kanban' {
    return this.$store.getters['PreferencesModule/getDamageView'];
  }

  set viewMode(value: 'list' | 'kanban') {
    this.$store.dispatch(`PreferencesModule/${PreferencesAction.setDamageView}`, value);
  }

  get filteredDamages(): IDamage[] {
    let filtered = [...this.damages];

    if (this.damageFilter.users && this.damageFilter.users.length > 0) {
      const userIds = this.damageFilter.users.map((u: IUser) => u.id);
      filtered = filtered.filter((damage) => damage.user && userIds.includes(damage.user.id));
    }

    if (this.damageFilter.products && this.damageFilter.products.length > 0) {
      const productIds = this.damageFilter.products.map((p: IProduct) => p.id);
      filtered = filtered.filter(
        (damage) => damage.product && productIds.includes(damage.product.id),
      );
    }

    if (this.damageFilter.tags && this.damageFilter.tags.length > 0) {
      const tagIds = this.damageFilter.tags.map((t: ITag) => t.id);
      filtered = filtered.filter((damage) => {
        if (!damage.tags || damage.tags.length === 0) return false;
        return damage.tags.some((tag) => tagIds.includes(tag.id));
      });
    }

    if (this.searchQuery.length > 1) {
      const searchLower = this.searchQuery.toLowerCase();
      filtered = filtered.filter((damage: IDamage) => {
        const searchableText = [
          damage.customer || '',
          damage.policyNumber || '',
          damage.location || '',
          damage.affectedItem || '',
          damage.description || '',
          damage.regulationType,
          damage.product?.name || '',
          damage.value?.toString() || '',
        ]
          .join(' ')
          .toLowerCase();

        return searchableText.includes(searchLower);
      });
    }

    return filtered;
  }

  /** Summe der Schadenhöhen über alle gefilterten Schäden. */
  get sumValue(): number {
    return this.filteredDamages.reduce((acc, damage) => {
      const value = Number(damage.value);
      return acc + (Number.isFinite(value) ? value : 0);
    }, 0);
  }

  /** Anzahl offener Schäden (neu, in Bearbeitung, wartend, eingereicht). */
  get sumOpen(): number {
    const openStatuses = [
      DamageStatus.NEW,
      DamageStatus.IN_PROGRESS,
      DamageStatus.PENDING,
      DamageStatus.SUBMITTED,
    ];
    return this.filteredDamages.filter((damage) => openStatuses.includes(damage.status)).length;
  }

  /** Anzahl abgeschlossener Schäden. */
  get sumCompleted(): number {
    return this.filteredDamages.filter((damage) => damage.status === DamageStatus.COMPLETED).length;
  }

  /** Anzahl abgelehnter Schäden. */
  get sumDeclined(): number {
    return this.filteredDamages.filter((damage) => damage.status === DamageStatus.DECLINED).length;
  }

  async mounted() {
    await this.loadDamages();
    await this.handleRouteForDamage();
  }

  @Watch('$route')
  async onRouteChanged() {
    await this.handleRouteForDamage();
  }

  async handleRouteForDamage() {
    const id = this.$route.params.id;
    if (!id) return;

    // Falls in der Liste vorhanden, direkt verwenden; sonst laden
    let damage = this.damages.find((d) => d.id === id) || null;
    if (!damage) {
      try {
        const res = await DamageApi.getDamage(id);
        damage = res.data;
      } catch (e) {
        this.$toastr.e('Schaden nicht gefunden');
        this.$router.replace({ name: 'damages' });
        return;
      }
    }

    this.selectedDamage = damage;
    this.$nextTick(() => {
      (this.$refs['edit-damage-modal'] as any).$refs['edit-damage'].show();
    });
  }

  openDamageById = async (id: string) => {
    const res = await DamageApi.getDamage(id);
    this.selectedDamage = res.data;
    (this.$refs['edit-damage-modal'] as any).$refs['edit-damage'].show();
    if (this.$route.name !== 'damages.edit' || this.$route.params.id !== id) {
      this.$router.push({ name: 'damages.edit', params: { id } });
    }
  };

  onDamageUpdated = () => {
    this.loadDamages();
  };

  onModalClosed() {
    this.selectedDamage = null;
    if (this.$route.name === 'damages.edit') {
      this.$router.replace({ name: 'damages' });
    }
  }

  async loadDamages() {
    try {
      this.isLoading = true;
      const showDemo = this.$store.getters['PreferencesModule/getShowDemo'];
      const response = await DamageApi.getDamages({
        agency: this.user.agency,
        showDemo,
      });
      this.damages = response.data;
    } catch (e) {
      console.error('Error loading damages', e);
      this.$toastr.e('Fehler beim Laden der Schäden');
    } finally {
      this.isLoading = false;
    }
  }
}
